import { useState } from 'react'
import { UserRole } from '@numeo/types-core'
import axios, { isAxiosError } from 'axios'
import { useAppSelector } from 'hooks'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import Typography from '@mui/joy/Typography'
import Button from '@mui/joy/Button'
import Input from '@mui/joy/Input'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import Alert from '@mui/joy/Alert'
import IconButton from '@mui/joy/IconButton'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

interface TeamMember {
    email: string
    role: UserRole
}

interface TeamOnboardingModalProps {
    open: boolean
    onClose: () => void
}

export const TeamOnboardingModal = ({ open, onClose }: TeamOnboardingModalProps) => {
    const projectName = useAppSelector((state) => state.auth.application?.projectName)
    const [members, setMembers] = useState<TeamMember[]>([{ email: '', role: UserRole.DISPATCHER }])
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)
    const [inviteLink, setInviteLink] = useState<string | null>(null)
    const [linkCopied, setLinkCopied] = useState(false)

    const handleAddMember = () => {
        setMembers((prev) => [...prev, { email: '', role: UserRole.DISPATCHER }])
    }

    const handleRemoveMember = (index: number) => {
        setMembers((prev) => prev.filter((_, i) => i !== index))
    }

    const handleEmailChange = (index: number, email: string) => {
        setMembers((prev) => prev.map((m, i) => (i === index ? { ...m, email } : m)))
    }

    const handleRoleChange = (index: number, role: UserRole) => {
        setMembers((prev) => prev.map((m, i) => (i === index ? { ...m, role } : m)))
    }

    const handleSendInvitations = async () => {
        const validMembers = members.filter((m) => m.email.trim())
        if (validMembers.length === 0) {
            setError('Please enter at least one email address.')
            return
        }

        setSending(true)
        setError(null)

        try {
            await axios.post('/application/invite', {
                projectName,
                emails: validMembers.map((m) => m.email.trim()),
                role: validMembers[0].role,
                expiresInDays: 7,
                type: 'private',
            })
            setSuccess(true)
        } catch (err) {
            setError(isAxiosError(err) ? err.response?.data?.message || 'Failed to send invitations' : 'An unexpected error occurred')
        } finally {
            setSending(false)
        }
    }

    const handleGenerateLink = async () => {
        setSending(true)
        setError(null)

        try {
            const response = await axios.post('/application/invite-anybody', {
                role: UserRole.DISPATCHER,
                expiresInDays: 7,
                type: 'public',
            })
            setInviteLink(response.data.data?.shareLink || null)
        } catch (err) {
            setError(isAxiosError(err) ? err.response?.data?.message || 'Failed to generate link' : 'An unexpected error occurred')
        } finally {
            setSending(false)
        }
    }

    const handleCopyLink = () => {
        if (inviteLink) {
            navigator.clipboard.writeText(inviteLink)
            setLinkCopied(true)
            setTimeout(() => setLinkCopied(false), 2000)
        }
    }

    const handleClose = () => {
        localStorage.removeItem('showTeamOnboarding')
        onClose()
    }

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog sx={{ maxWidth: 520, width: '100%', p: 3 }}>
                <Typography level="h3" sx={{ mb: 0.5 }}>
                    Add Your Team
                </Typography>
                <Typography level="body-sm" sx={{ mb: 3, color: 'neutral.500' }}>
                    Invite your dispatchers and admins to collaborate on Numeo.
                </Typography>

                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success ? (
                    <div className="text-center py-4">
                        <Alert color="success" sx={{ mb: 3 }}>
                            Invitations sent successfully!
                        </Alert>
                        <Button variant="solid" color="primary" onClick={handleClose}>
                            Go to Dashboard
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Email invitation rows */}
                        <div className="flex flex-col gap-2 mb-3">
                            {members.map((member, index) => (
                                <div key={index} className="flex gap-2 items-center">
                                    <Input
                                        placeholder="Email address"
                                        type="email"
                                        value={member.email}
                                        onChange={(e) => handleEmailChange(index, e.target.value)}
                                        sx={{ flex: 1 }}
                                    />
                                    <Select value={member.role} onChange={(_, val) => val !== null && handleRoleChange(index, val)} sx={{ minWidth: 130 }}>
                                        <Option value={UserRole.ADMIN}>Admin</Option>
                                        <Option value={UserRole.DISPATCHER}>Dispatcher</Option>
                                    </Select>
                                    {members.length > 1 && (
                                        <IconButton variant="plain" color="neutral" size="sm" onClick={() => handleRemoveMember(index)}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </div>
                            ))}
                        </div>

                        <Button
                            variant="plain"
                            color="neutral"
                            size="sm"
                            startDecorator={<AddIcon />}
                            onClick={handleAddMember}
                            sx={{ mb: 3, alignSelf: 'flex-start' }}
                        >
                            Add another
                        </Button>

                        <Button variant="solid" color="primary" fullWidth loading={sending} onClick={handleSendInvitations} sx={{ mb: 1.5 }}>
                            Send Invitations
                        </Button>

                        {/* Divider */}
                        <div className="flex items-center gap-3 my-2">
                            <div className="flex-1 h-px bg-gray-200" />
                            <Typography level="body-xs" sx={{ color: 'neutral.400' }}>
                                or
                            </Typography>
                            <div className="flex-1 h-px bg-gray-200" />
                        </div>

                        {/* Generate invitation link */}
                        {inviteLink ? (
                            <div className="flex gap-2 items-center mt-2">
                                <Input value={inviteLink} readOnly sx={{ flex: 1 }} size="sm" />
                                <Button
                                    variant="outlined"
                                    color={linkCopied ? 'success' : 'neutral'}
                                    size="sm"
                                    startDecorator={<ContentCopyIcon fontSize="small" />}
                                    onClick={handleCopyLink}
                                >
                                    {linkCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        ) : (
                            <Button variant="outlined" color="neutral" fullWidth onClick={handleGenerateLink} loading={sending} sx={{ mt: 1 }}>
                                Generate Invitation Link
                            </Button>
                        )}

                        <Button variant="plain" color="neutral" fullWidth onClick={handleClose} sx={{ mt: 2 }}>
                            Skip for now
                        </Button>
                    </>
                )}
            </ModalDialog>
        </Modal>
    )
}
